// Phase 7 · Daily Operations Compliance — read helpers + status shaping.
//
// All functions degrade gracefully on the pre-Phase-7 schema (42P01/42703/
// PGRST202 errors are swallowed in favour of a non-blocking empty state).

import { supabase, isSupabaseConfigured } from "@/lib/supabase";

/** Per-child clearance for today. Returns { cleared, blockers: [...] }. */
export async function fetchChildClearance(childId) {
  if (!isSupabaseConfigured || !childId) return { cleared: true, blockers: [] };
  const { data, error } = await supabase.rpc("rpc_child_clearance_today", {
    p_child_id: childId,
  });
  if (error) {
    if (["42883", "42P01", "42703", "PGRST202"].includes(error.code)) {
      return { cleared: true, blockers: [], _unsupported: true };
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn("[dailyOpsCompliance] rpc_child_clearance_today error", error);
    }
    throw error;
  }
  const rows = Array.isArray(data) ? data : [data].filter(Boolean);
  if (!rows.length) return { cleared: true, blockers: [] };
  const cleared = rows[0]?.out_cleared !== false;
  const blockers = rows
    .filter((r) => r.out_blocker_type_id)
    .map((r) => ({
      typeId: r.out_blocker_type_id,
      typeLabel: r.out_blocker_type_label,
      reason: r.out_blocker_reason,
      recordId: r.out_blocker_record_id,
      expiresOn: r.out_blocker_expires_on,
    }));
  return { cleared, blockers };
}

/** Bulk clearance for an array of children — used by the teacher roster
 * and the admin Today panel. Falls back gracefully if unsupported. */
export async function fetchClearanceForChildren(childIds) {
  if (!childIds?.length) return new Map();
  const out = new Map();
  // PostgREST has no bulk RPC array param for our shape — N small calls
  // is acceptable at typical org scale (<60 children).
  await Promise.all(childIds.map(async (id) => {
    try {
      out.set(id, await fetchChildClearance(id));
    } catch {
      out.set(id, { cleared: true, blockers: [], _error: true });
    }
  }));
  return out;
}

/** Org-wide clearance summary — admin "Today" panel. */
export async function fetchClearanceSummary() {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc("rpc_clearance_summary_today");
  if (error) {
    if (["42883", "42P01", "42703", "PGRST202"].includes(error.code)) return [];
    throw error;
  }
  return (data || []).map((r) => ({
    childId: r.out_child_id,
    childName: r.out_child_name,
    classroomId: r.out_classroom_id,
    classroomName: r.out_classroom_name,
    blockerCount: r.out_blocker_count,
    blockerSummary: r.out_blocker_summary,
  }));
}

/** Today + 6-day classroom ratio compliance grid. */
export async function fetchRatioCompliance() {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase.rpc("rpc_classroom_ratio_compliance_today");
  if (error) {
    if (["42883", "42P01", "42703", "PGRST202"].includes(error.code)) return [];
    throw error;
  }
  return (data || []).map((r) => ({
    classroomId: r.out_classroom_id,
    classroomName: r.out_classroom_name,
    requiredRatio: r.out_required_ratio,
    day: r.out_day,
    headCount: r.out_head_count,
    caregiversCount: r.out_caregivers_count,
    caregiversCertified: r.out_caregivers_certified,
    caregiversUncertified: r.out_caregivers_uncertified,
    ratioBreach: !!r.out_ratio_breach,
    certBreach: !!r.out_cert_breach,
  }));
}

/** Group ratio rows by classroom, ordered by day. */
export function groupRatioByClassroom(rows) {
  const map = new Map();
  for (const r of rows) {
    if (!map.has(r.classroomId)) {
      map.set(r.classroomId, { classroomId: r.classroomId, classroomName: r.classroomName, requiredRatio: r.requiredRatio, days: [] });
    }
    map.get(r.classroomId).days.push(r);
  }
  for (const cls of map.values()) {
    cls.days.sort((a, b) => (a.day < b.day ? -1 : 1));
  }
  return Array.from(map.values());
}

export const CLEARANCE_CHIP = {
  cleared:       { label: "Cleared",       tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  action_needed: { label: "Action needed", tone: "border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]" },
  unknown:       { label: "—",             tone: "border-neutral-200 bg-neutral-50 text-neutral-500" },
};
